/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Card from "@mui/material/Card";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import Tooltip from "@mui/material/Tooltip";
import Icon from "@mui/material/Icon";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

// Analytics application components
import PagesHeaderCell from "layouts/applications/analytics/components/PagesHeaderCell";
import PagesBodyCell from "layouts/applications/analytics/components/PagesBodyCell";
import { useState, useEffect } from "react";
import { collection, query, orderBy, limit, getDocs, getFirestore, where } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import PropTypes from 'prop-types'
import { useNavigate } from "react-router-dom";

function Pages({ userId }) {

  const [latestPatients, setLatestPatients] = useState([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const db = getFirestore();
  const navigate = useNavigate();

  const handleIconClick = (patientName) => {
    // Navigate to the patient details page with the patientId
    navigate(`/ecommerce/referral?patientName=${patientName}`);
  };

  function formatRecordingTime(seconds) {
    if (!seconds && seconds !== 0) return "00:00";

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = (seconds % 60).toFixed(0);;
    return `${minutes}m ${remainingSeconds}s`;
  }

  useEffect(() => {
    const auth = getAuth();
    onAuthStateChanged(auth, (user) => {
      if (user) {
        if (userId) {
          fetchLatestPatients();
        }
      }
    });
  }, [userId]); // Dependency array

  const fetchLatestPatients = async () => {
    const summariesRef = collection(db, `users/${userId}/summaries`);
    const q = query(summariesRef, orderBy("lastVisit", "desc"), limit(15));
  
    try {
      const querySnapshot = await getDocs(q);
      const latestPatientsData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        let formattedDate = data.lastVisit;
        if (data.lastVisit?.toDate) { // Check if toDate function exists
          formattedDate = data.lastVisit.toDate().toLocaleDateString('en-US');
        }
        const recordingTimeFormatted = formatRecordingTime(data.lastRecordingTime)

        return {
          id: doc.id,
          ...data,
          lastVisit: formattedDate,
          recordingTime: recordingTimeFormatted
        };
      });

      console.log("Fetched latest patients:", latestPatientsData); // Log fetched data
      setLatestPatients(latestPatientsData);
      setIsDataLoaded(true);
    } catch (error) {
      console.error("Error fetching latest patients:", error);
    }
  };
  


  return (
    <Card sx={{ height: '440px', overflowY: 'auto' }}>
      <SoftBox display="flex" justifyContent="space-between" alignItems="center" pt={3} px={2} >
        <SoftTypography pl={1} variant="h5">Recent Patient Visits</SoftTypography>
        <SoftBox >
          <Tooltip title={isDataLoaded ? "Data is up to date" : "Data is not up to date"} placement="left">
            <SoftButton variant="outlined" color={isDataLoaded ? "success" : "error"} size="small" circular iconOnly  sx={{ mr: 2 }}>
              <Icon sx={{ fontWeight: "bold" }}>{isDataLoaded ? "done" : "close"}</Icon>
            </SoftButton>
          </Tooltip>
          <SoftButton variant="contained" color="dark" size="small" onClick={fetchLatestPatients}>
              Reload
          </SoftButton>
        </SoftBox>
      </SoftBox>
      <SoftBox py={1} px={2} mb={2} mt={2}>
        <TableContainer sx={{ boxShadow: "none" }}>
          <Table>
            <SoftBox component="thead">
              <TableRow>
                <PagesHeaderCell>patient name</PagesHeaderCell>
                <PagesHeaderCell>visit date</PagesHeaderCell>
                <PagesHeaderCell>visit duration </PagesHeaderCell>
                
              </TableRow>
            </SoftBox>
            <TableBody>
            {latestPatients.length > 0 ? (
              latestPatients.map((patient, index) => (
                <PagesBodyCell
                  key={patient.id}
                  rows={[
                    `${index + 1}. ${patient.id}`,
                    patient.lastVisit, 
                    patient.recordingTime
                  ]}
                  noBorder={index === latestPatients.length - 1}
                  onIconClick={() => handleIconClick(patient.id)}
                />
              ))
            ) : (
              null
            )}
          </TableBody>
          </Table>
        </TableContainer>
        {latestPatients.length < 1 && (
          <SoftBox display="flex" flexDirection="column" justifyContent="center" alignItems="center" mt={10}>
            <SoftTypography variant="h5" fontWeight="medium">
              No Patients data yet 
            </SoftTypography>
            <SoftTypography variant="h6" fontWeight="medium" color="text">
              Record your first conversation 
            </SoftTypography>
          </SoftBox>
        )}
      </SoftBox>
    </Card>
  );
}

Pages.propTypes = {
  userId: PropTypes.string.isRequired,
};

export default Pages;
